import mqtt, { MqttClient } from 'mqtt';

export interface SupportRequestNotification {
  type: 'NEW_SUPPORT_REQUEST' | 'SUPPORT_REQUEST_STATUS';
  requestId: number;
  requesterId: number;
  requesterName: string;
  subject: string;
  category: string;
  status?: string;
  adminId?: number;
  adminName?: string;
  timestamp: string;
}

class MQTTSupportService {
  private client: MqttClient | null = null;
  private isConnected: boolean = false;

  // MQTT Broker Configuration - Secure WebSocket
  private getBrokerUrl(): string {
    // Priority 1: Use environment variable if set
    if (process.env.REACT_APP_MQTT_URL) {
      console.log('🔧 [MQTT Support] Using REACT_APP_MQTT_URL:', process.env.REACT_APP_MQTT_URL);
      return process.env.REACT_APP_MQTT_URL;
    }

    // Priority 2: Use the standard admin MQTT broker URL
    const brokerUrl = 'wss://admin.bonyad-hub.com/mqtt';

    console.log('🔧 [MQTT Support] Broker URL Configuration:');
    console.log('   - URL:', brokerUrl);
    console.log('   - Protocol: WSS (Secure WebSocket)');
    console.log('   - Proxy: Nginx reverse proxy');
    console.log('   - Security: TLS/SSL enabled ✅');

    return brokerUrl;
  }

  // Topics
  private readonly SUPPORT_REQUESTS_TOPIC = 'support/requests';
  private readonly SUPPORT_ROOM_TOPIC_PREFIX = 'chat/room/';
  private readonly PRESENCE_ADMIN_TOPIC = 'presence/admin/all';  // For online/offline status

  // Callbacks
  private onNewRequestCallbacks: ((notification: SupportRequestNotification) => void)[] = [];
  private onStatusUpdateCallbacks: ((notification: SupportRequestNotification) => void)[] = [];
  private roomCallbacks: Map<string, (message: any) => void> = new Map();
  private connectionStatusCallbacks: ((connected: boolean) => void)[] = [];
  private onOnlineStatusCallbacks: ((statusUpdate: any) => void)[] = [];

  private currentAuthToken: string | null = null;

  connect(authToken?: string): void {
    // If token provided is different from current, or client not connected, we reconnect
    if (this.client?.connected && this.currentAuthToken === authToken) {
      console.log('✅ [MQTT Support] Already connected with same token');
      return;
    }

    // Store current token
    this.currentAuthToken = authToken || null;

    // Close existing client if any
    if (this.client) {
      console.log('🔄 [MQTT Support] Closing existing client to reconnect...');
      try {
        this.client.end(true);
      } catch (e) {
        // Ignore errors when ending
      }
      this.client = null;
    }

    const brokerUrl = this.getBrokerUrl();
    const clientId = `customer-support-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📡 [MQTT Support] Connecting to:', brokerUrl);
    console.log('   - Client ID:', clientId);
    console.log('   - Auth Token:', authToken ? 'Provided' : 'Not provided');

    const options: any = {
      clientId,
      clean: true,
      reconnectPeriod: 5000,
      connectTimeout: 30000,
      keepalive: 60,
      protocol: 'wss',
      rejectUnauthorized: false,
    };

    // Verify options use WSS
    if (options.protocol !== 'wss') {
      console.error('❌ [MQTT Support] ERROR: Options protocol is not WSS!', options.protocol);
      options.protocol = 'wss';
    }

    console.log('   - Options Protocol:', options.protocol, '(should be wss) ✅');

    // Add authentication if token is provided
    if (authToken) {
      options.username = authToken;
      options.password = ''; // Empty password for JWT auth
      console.log('   - Authentication: JWT token provided');
    } else {
      console.warn('   ⚠️  No authentication token provided');
    }

    try {
      this.client = mqtt.connect(brokerUrl, options);

      this.client.on('connect', (packet) => {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [MQTT Support] ✅✅✅ CONNECTED SUCCESSFULLY! ✅✅✅');
        console.log('   - Broker URL:', brokerUrl);
        console.log('   - Client ID:', clientId);
        console.log('   - Protocol: WSS (Secure WebSocket) ✅');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        this.isConnected = true;
        this.subscribeToTopics();
        this.notifyConnectionStatus(true);
      });

      this.client.on('error', (error) => {
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.error('❌ [MQTT Support] ❌❌❌ CONNECTION ERROR ❌❌❌');
        console.error('   - Broker URL:', brokerUrl);
        console.error('   - Error:', error);
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        this.isConnected = false;
        this.notifyConnectionStatus(false);
      });

      this.client.on('close', () => {
        console.log('📡 [MQTT Support] Connection closed');
        this.isConnected = false;
        this.notifyConnectionStatus(false);
      });

      this.client.on('offline', () => {
        console.log('📴 [MQTT Support] Client went offline');
        this.isConnected = false;
        this.notifyConnectionStatus(false);
      });

      this.client.on('reconnect', () => {
        console.log('🔄 [MQTT Support] Reconnecting...');
      });

      this.client.on('message', (topic, message) => {
        this.handleMessage(topic, message.toString());
      });

      console.log('✅ [MQTT Support] Connection initiated');
    } catch (error) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ [MQTT Support] ❌❌❌ FAILED TO CREATE CONNECTION ❌❌❌');
      console.error('   - Broker URL:', brokerUrl);
      console.error('   - Error:', error);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      this.isConnected = false;
      this.notifyConnectionStatus(false);
    }
  }

  /**
   * Publish a message to a specific topic
   */
  publish(topic: string, message: any): void {
    if (!this.client || !this.client.connected) {
      console.warn('⚠️ [MQTT Support] Cannot publish: Client not connected');
      return;
    }

    const payload = typeof message === 'string' ? message : JSON.stringify(message);
    this.client.publish(topic, payload, { qos: 1 }, (err) => {
      if (err) {
        console.error(`❌ [MQTT Support] Failed to publish to ${topic}:`, err);
      } else {
        console.log(`✅ [MQTT Support] Published message to ${topic}`);
      }
    });
  }

  /**
   * Send a chat message via MQTT (alternative to HTTP)
   */
  publishChatMessage(roomId: string, messageData: any): void {
    const topic = `${this.SUPPORT_ROOM_TOPIC_PREFIX}${roomId}`;
    this.publish(topic, messageData);
  }

  /**
   * Notify all connection status callbacks
   */
  private notifyConnectionStatus(connected: boolean): void {
    this.connectionStatusCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('❌ [MQTT Support] Error in connection status callback:', error);
      }
    });
  }

  private subscribeToTopics(): void {
    if (!this.client) {
      console.warn('⚠️ [MQTT Support] Cannot subscribe: Client is null');
      return;
    }

    if (!this.client.connected) {
      console.warn('⚠️ [MQTT Support] Cannot subscribe: Client not connected');
      return;
    }

    console.log('📡 [MQTT Support] Subscribing to topics...');

    // Subscribe to support request notifications
    this.client.subscribe(this.SUPPORT_REQUESTS_TOPIC, { qos: 1 }, (err, granted) => {
      if (err) {
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.error('❌ [MQTT Support] Failed to subscribe to support requests:', err);
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      } else {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [MQTT Support] ✅✅✅ SUBSCRIBED TO SUPPORT REQUESTS ✅✅✅');
        console.log('   - Topic:', this.SUPPORT_REQUESTS_TOPIC);
        console.log('   - Granted:', JSON.stringify(granted, null, 2));
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }
    });

    // Subscribe to admin presence for online/offline status
    this.client.subscribe(this.PRESENCE_ADMIN_TOPIC, { qos: 1 }, (err, granted) => {
      if (err) {
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.error('❌ [MQTT Support] Failed to subscribe to presence/admin/all:', err);
        console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      } else {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [MQTT Support] ✅✅✅ SUBSCRIBED TO PRESENCE ✅✅✅');
        console.log('   - Topic: presence/admin/all');
        console.log('   - Granted:', JSON.stringify(granted, null, 2));
        console.log('   - Ready to receive USER_ONLINE/USER_OFFLINE messages');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }
    });
  }

  /**
   * Subscribe to a specific support chat room
   */
  subscribeToSupportRoom(roomId: string, onMessage: (message: any) => void): void {
    if (!this.client || !this.client.connected) {
      console.error('❌ [MQTT Support] Client not connected');
      return;
    }

    const topic = `${this.SUPPORT_ROOM_TOPIC_PREFIX}${roomId}`;

    // Store callback for this room
    this.roomCallbacks.set(roomId, onMessage);

    this.client.subscribe(topic, { qos: 1 }, (err) => {
      if (err) {
        console.error(`❌ [MQTT Support] Failed to subscribe to room ${roomId}:`, err);
      } else {
        console.log(`✅ [MQTT Support] Subscribed to support room: ${topic}`);
      }
    });
  }

  /**
   * Unsubscribe from a support chat room
   */
  unsubscribeFromSupportRoom(roomId: string): void {
    if (!this.client || !this.client.connected) return;

    const topic = `${this.SUPPORT_ROOM_TOPIC_PREFIX}${roomId}`;

    this.roomCallbacks.delete(roomId);

    this.client.unsubscribe(topic, (err) => {
      if (err) {
        console.error(`❌ [MQTT Support] Failed to unsubscribe from room ${roomId}:`, err);
      } else {
        console.log(`✅ [MQTT Support] Unsubscribed from support room: ${topic}`);
      }
    });
  }

  private handleMessage(topic: string, message: string): void {
    try {
      // Check if this is a chat room message
      if (topic.startsWith(this.SUPPORT_ROOM_TOPIC_PREFIX)) {
        const roomId = topic.replace(this.SUPPORT_ROOM_TOPIC_PREFIX, '');
        const callback = this.roomCallbacks.get(roomId);
        if (callback) {
          const data = JSON.parse(message);
          callback(data);
        }
        return;
      }

      // Handle support request notifications
      if (topic === this.SUPPORT_REQUESTS_TOPIC) {
        const data: SupportRequestNotification = JSON.parse(message);

        if (data.type === 'NEW_SUPPORT_REQUEST') {
          console.log('🆘 [MQTT Support] New support request:', data);
          this.onNewRequestCallbacks.forEach(callback => callback(data));
        } else if (data.type === 'SUPPORT_REQUEST_STATUS') {
          console.log('📊 [MQTT Support] Status update:', data);
          this.onStatusUpdateCallbacks.forEach(callback => callback(data));
        }
        return;
      }

      // Handle admin presence notifications (online/offline status)
      if (topic === this.PRESENCE_ADMIN_TOPIC) {
        const data = JSON.parse(message);
        console.log('📡 [MQTT Support] Admin presence update:', data.type, '- User:', data.userName, '(ID:', data.userId + ')');

        // Normalize the status update
        const statusUpdate = {
          type: data.type,
          userId: data.userId,
          userName: data.userName,
          userRole: data.userRole,
          phoneNumber: data.phoneNumber,
          timestamp: data.timestamp,
          isOnline: data.type === 'USER_ONLINE' || data.isOnline === true,
          lastSeen: data.lastSeen || null,
        };

        // Call all registered online status callbacks
        this.onOnlineStatusCallbacks.forEach(callback => {
          try {
            callback(statusUpdate);
          } catch (error) {
            console.error('❌ [MQTT Support] Error in online status callback:', error);
          }
        });
        return;
      }
    } catch (error) {
      console.error('❌ [MQTT Support] Failed to parse message:', error);
    }
  }

  /**
   * Register callback for new support requests
   */
  onNewSupportRequest(callback: (notification: SupportRequestNotification) => void): void {
    if (!this.onNewRequestCallbacks.includes(callback)) {
      this.onNewRequestCallbacks.push(callback);
    }
  }

  offNewSupportRequest(callback: (notification: SupportRequestNotification) => void): void {
    this.onNewRequestCallbacks = this.onNewRequestCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Register callback for status updates
   */
  onStatusUpdate(callback: (notification: SupportRequestNotification) => void): void {
    if (!this.onStatusUpdateCallbacks.includes(callback)) {
      this.onStatusUpdateCallbacks.push(callback);
    }
  }

  offStatusUpdate(callback: (notification: SupportRequestNotification) => void): void {
    this.onStatusUpdateCallbacks = this.onStatusUpdateCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Register callback for connection status changes
   */
  onConnectionStatusChange(callback: (connected: boolean) => void): void {
    if (!this.connectionStatusCallbacks.includes(callback)) {
      this.connectionStatusCallbacks.push(callback);
    }
    // Immediately call with current status
    callback(this.isConnectedToBroker());
  }

  offConnectionStatusChange(callback: (connected: boolean) => void): void {
    this.connectionStatusCallbacks = this.connectionStatusCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Register callback for online status updates (from presence/admin/all)
   */
  onOnlineStatusUpdate(callback: (statusUpdate: any) => void): void {
    if (!this.onOnlineStatusCallbacks.includes(callback)) {
      this.onOnlineStatusCallbacks.push(callback);
    }
    console.log('📡 [MQTT Support] Online status callback registered');
  }

  offOnlineStatusUpdate(callback: (statusUpdate: any) => void): void {
    this.onOnlineStatusCallbacks = this.onOnlineStatusCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove all callbacks
   */
  removeAllCallbacks(): void {
    this.onNewRequestCallbacks = [];
    this.onStatusUpdateCallbacks = [];
    this.connectionStatusCallbacks = [];
    this.onOnlineStatusCallbacks = [];
    this.roomCallbacks.clear();
    console.log('🧹 [MQTT Support] All callbacks removed');
  }

  disconnect(): void {
    if (this.client) {
      this.client.end();
      this.client = null;
      this.isConnected = false;
      this.roomCallbacks.clear();
      console.log('📡 [MQTT Support] Disconnected');
    }
  }

  isConnectedToBroker(): boolean {
    return this.isConnected && this.client !== null && (this.client.connected || false);
  }
}

// Export singleton instance
export const mqttSupportService = new MQTTSupportService();

